import { CheckBox, CheckBoxOutlineBlank } from '@mui/icons-material'
import { Autocomplete, Checkbox, FormControlLabel, Tooltip, Typography, Radio } from '@mui/material'
import TextField from '@mui/material/TextField'
import React, { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { StyledListItem } from '../../../styles/ProfileViewStyles'
import { useGetLightUsersQuery } from '../../../store/redux/reducers/usersSlice'
import { ProjectAcronym, useGetParentProjectsQuery } from '../../../store/redux/reducers/projectsSlice'
import { RequestProps, User } from '../../../store/types'
import FilterMenu from '../FilterMenu'
import CollapsibleSection from '../../Button/CollapsibleSection'
import { debounce, isEqual } from 'lodash'
import InfiniteScroll from 'react-infinite-scroll-component'
import RoleType from '../../../store/types/enums/RoleType'
import ProjectStatus from '../../../store/types/enums/ProjectStatus'
import defaultProps from '../../../config/defaultProps'
import { useDebouncedScroll } from '../../../store/utils/useDebouncedScroll'
import { useLeftPadding } from '../../../store/redux/hooks'

export type FilterType = {
  projectHierarchy: 'parent' | 'child' | null
  privacy: string[]
  checkedStatuses: ProjectStatus[]
  leaders: string[]
  parents: string[]
  favorite: boolean
  operational: null | boolean
}

export const initialFilters: FilterType = {
  projectHierarchy: null,
  privacy: [],
  checkedStatuses: [],
  leaders: [],
  parents: [],
  favorite: false,
  operational: null,
}

const FilterProjectsMenu = ({
  requestProps,
  setRequestProps,
  setPage,
}: {
  requestProps: RequestProps
  setRequestProps: React.Dispatch<React.SetStateAction<RequestProps>>
  setPage: Dispatch<SetStateAction<number>>
}) => {
  const { t } = useTranslation()
  const leftPadding = useLeftPadding()
  const [selectedFilters, setSelectedFilters] = useState<FilterType>(initialFilters)
  const [parentName, setParentName] = useState<ProjectAcronym[]>([])
  const [displayedLeaders, setDisplayedLeaders] = useState<User[]>([])
  const [hasMore, setHasMore] = useState<boolean>(true)
  const [leaderRequestProps, setLeaderRequestProps] = useState<RequestProps>({
    page: 0,
    size: 20,
    'roles.name': RoleType.ProjectLeader,
    employeeStatus: 'ACTIVE',
  })
  const { data: leaders } = useGetLightUsersQuery(leaderRequestProps)
  const [requestParentProjectProps, setRequestParentProjectProps] = useState<RequestProps>(defaultProps)

  const [parentProjectPage, setParentProjectPage] = useState(0)

  const { data: projectsResult, isLoading: isParentsProjectLoading } =
    useGetParentProjectsQuery(requestParentProjectProps)
  const [displayedProjects, setDisplayedProjects] = useState<ProjectAcronym[]>([])

  const onParentProjectSelectBottomScroll = () => {
    if (!projectsResult?.last) {
      handleFetchParentProjectMoreData()
    }
  }

  const onScroll = useDebouncedScroll(onParentProjectSelectBottomScroll)

  const handleParentProjectSearch = debounce((value: string) => {
    setRequestParentProjectProps({ page: 0, size: 20, acronym: value })
    setParentProjectPage(0)
  }, 300)

  const handleFetchParentProjectMoreData = () => {
    setParentProjectPage(prev => prev + 1)
    setRequestParentProjectProps({ ...requestParentProjectProps, page: parentProjectPage })
  }

  useMemo(() => {
    if (projectsResult && projectsResult.number === 0) {
      setDisplayedProjects(projectsResult.content)
    } else if (projectsResult) {
      setDisplayedProjects(prev => [...prev].concat(projectsResult.content))
    }
  }, [projectsResult])

  function removeElementFromList<T>(value: T, array: T[]): T[] {
    return array.filter(e => e !== value)
  }

  const handleProjectHierarchyChange = (projectHierarchy: 'parent' | 'child') => {
    setSelectedFilters(prevFilters => ({
      ...prevFilters,
      projectHierarchy: prevFilters.projectHierarchy === projectHierarchy ? null : projectHierarchy,
    }))
  }

  const handlePrivacyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target
    setSelectedFilters(prevFilters => {
      if (checked) {
        return {
          ...prevFilters,
          privacy: [...prevFilters.privacy, name],
        }
      } else {
        return {
          ...prevFilters,
          privacy: removeElementFromList(name, prevFilters.privacy),
        }
      }
    })
  }

  const handleStatusChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = event.target

    if (checked) {
      setSelectedFilters(prevFilters => ({
        ...prevFilters,
        checkedStatuses: [...prevFilters.checkedStatuses, value as ProjectStatus],
      }))
    } else {
      setSelectedFilters(prevFilters => ({
        ...prevFilters,
        checkedStatuses: removeElementFromList(value as ProjectStatus, prevFilters.checkedStatuses),
      }))
    }
  }

  const handleLeaderChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    const isChecked = event.target.checked

    if (isChecked) {
      setSelectedFilters(prevFilters => ({
        ...prevFilters,
        leaders: [...prevFilters.leaders, value],
      }))
    } else {
      setSelectedFilters(prevFilters => ({
        ...prevFilters,
        leaders: removeElementFromList(value, prevFilters.leaders),
      }))
    }
  }

  const handleFavoriteChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked
    setSelectedFilters(prevFilters => ({
      ...prevFilters,
      favorite: checked,
    }))
  }

  const handleFiltering = () => {
    const { isParent, ...requestPropsWithoutIsParent } = requestProps as RequestProps & { isParent?: boolean }

    const newProps: RequestProps = {
      ...requestPropsWithoutIsParent,
      page: 0,
      privacy: selectedFilters.privacy.length ? selectedFilters.privacy.toString() : undefined,
      projectStatus: selectedFilters.checkedStatuses.length ? selectedFilters.checkedStatuses.toString() : undefined,
      'leaders.leader.id': selectedFilters.leaders.length ? selectedFilters.leaders.toString() : undefined,
      'parent.id': selectedFilters.parents.length ? selectedFilters.parents.toString() : undefined,
    }

    if (selectedFilters.projectHierarchy === 'parent') {
      ;(newProps as any).isParent = true
    } else if (selectedFilters.projectHierarchy === 'child') {
      ;(newProps as any).isParent = false
    }

    if (selectedFilters.operational !== null) {
      newProps.operational = selectedFilters.operational
    }

    if (selectedFilters.favorite) {
      newProps.favorite = true
    }

    if (!isEqual(requestProps, newProps)) {
      setRequestProps(newProps)
      setPage(0)
    }
  }

  const handleClear = () => {
    setParentName([])
    setSelectedFilters(initialFilters)
    setPage(0)
    setRequestProps({ page: 0, size: requestProps.size, 'query:name,acronym': requestProps['query:name,acronym'] })
  }

  const handleFetchMoreData = () => {
    setLeaderRequestProps(prev => ({ ...prev, page: prev.page + 1 }))
  }

  const handleOperationalChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    type: 'operational' | 'nonOperational'
  ) => {
    const checked = event.target.checked
    setSelectedFilters(prevFilters => {
      if (type === 'operational') {
        const newOperational = checked ? true : prevFilters.operational === false ? false : null
        return {
          ...prevFilters,
          operational: newOperational === false && prevFilters.operational === false ? null : newOperational,
        }
      } else {
        const newOperational = checked ? false : prevFilters.operational === true ? true : null
        return {
          ...prevFilters,
          operational: newOperational === true && prevFilters.operational === true ? null : newOperational,
        }
      }
    })
  }

  useMemo(() => {
    if (leaders) {
      setHasMore(!leaders.last)
      setDisplayedLeaders(prev => [...prev].concat(leaders.content))
    }
  }, [leaders])

  useEffect(() => {
    if (leaders && leaders.last) {
      setHasMore(false)
    }
  }, [leaders])

  const icon = <CheckBoxOutlineBlank fontSize="small" />
  const checkedIcon = <CheckBox fontSize="small" />

  return (
    <FilterMenu filter={handleFiltering} clear={handleClear}>
      <CollapsibleSection text={t('page.reports.filterOnlyParents')}>
        <FormControlLabel
          sx={{ pl: leftPadding }}
          control={
            <Radio
              checked={selectedFilters.projectHierarchy === 'parent'}
              onClick={() => handleProjectHierarchyChange('parent')}
            />
          }
          label={t('page.reports.filterOnlyParents')}
        />
        <FormControlLabel
          sx={{ pl: leftPadding }}
          control={
            <Radio
              checked={selectedFilters.projectHierarchy === 'child'}
              onClick={() => handleProjectHierarchyChange('child')}
            />
          }
          label={t('page.reports.filterOnlyChildren')}
        />
      </CollapsibleSection>

      <CollapsibleSection text={t('page.projects.filterPopup.privacy')}>
        <FormControlLabel
          sx={{ pl: leftPadding }}
          control={
            <Checkbox checked={selectedFilters.privacy.includes('true')} onChange={handlePrivacyChange} name="true" />
          }
          label={t('page.projects.filterPopup.private')}
        />
        <FormControlLabel
          sx={{ pl: leftPadding }}
          control={
            <Checkbox checked={selectedFilters.privacy.includes('false')} onChange={handlePrivacyChange} name="false" />
          }
          label={t('page.projects.filterPopup.public')}
        />
      </CollapsibleSection>

      <CollapsibleSection text={t('page.projects.filterPopup.status')}>
        <FormControlLabel
          sx={{ pl: leftPadding }}
          control={
            <Checkbox
              checked={selectedFilters.checkedStatuses.includes('ACTIVE' as ProjectStatus)}
              onChange={handleStatusChange}
              value="ACTIVE"
            />
          }
          label={t('page.projects.filterPopup.active')}
        />
        <FormControlLabel
          sx={{ pl: leftPadding }}
          control={
            <Checkbox
              checked={selectedFilters.checkedStatuses.includes('INACTIVE' as ProjectStatus)}
              onChange={handleStatusChange}
              value="INACTIVE"
            />
          }
          label={t('page.projects.filterPopup.inactive')}
        />
        <FormControlLabel
          sx={{ pl: leftPadding }}
          control={
            <Checkbox
              checked={selectedFilters.checkedStatuses.includes('FINISHED' as ProjectStatus)}
              onChange={handleStatusChange}
              value="FINISHED"
            />
          }
          label={t('page.projects.filterPopup.finished')}
        />
        <FormControlLabel
          sx={{ pl: leftPadding }}
          control={
            <Checkbox
              checked={selectedFilters.checkedStatuses.includes('TRIAL' as ProjectStatus)}
              onChange={handleStatusChange}
              value="TRIAL"
            />
          }
          label={t('page.projects.filterPopup.trial')}
        />
      </CollapsibleSection>

      {leaders && (
        <CollapsibleSection text={t('page.projects.editProject.projectLeaders')}>
          <div
            style={{
              height: 150,
              overflowY: 'auto',
              overflowX: 'hidden',
            }}
            id="scrollableDiv"
          >
            <InfiniteScroll
              dataLength={displayedLeaders.length}
              next={handleFetchMoreData}
              hasMore={hasMore}
              loader={
                <span>
                  <i>{t('components.infiniteScroll.loading')}</i>
                </span>
              }
              scrollableTarget="scrollableDiv"
              style={{ overflowX: 'hidden', display: 'flex', flexDirection: 'column' }}
              endMessage={
                <Typography variant="body2" align="center" paddingY={2}>
                  <i>{t('components.infiniteScroll.endMessage.leaders')}</i>
                </Typography>
              }
            >
              {displayedLeaders.map((leader, _index) => (
                <Tooltip
                  title={`${leader.firstName} ${leader.lastName}`}
                  placement="right"
                  key={`${leader.id}-filtering-menu-item-tooltip`}
                >
                  <FormControlLabel
                    sx={{ pl: leftPadding }}
                    control={
                      <Checkbox
                        value={leader.id}
                        onChange={handleLeaderChange}
                        checked={selectedFilters.leaders.includes(leader.id)}
                      />
                    }
                    label={
                      <Typography
                        sx={{
                          overflow: 'hidden',
                          whiteSpace: 'nowrap',
                          textOverflow: 'ellipsis',
                        }}
                      >{`${leader.firstName} ${leader.lastName}`}</Typography>
                    }
                  />
                </Tooltip>
              ))}
            </InfiniteScroll>
          </div>
        </CollapsibleSection>
      )}

      <CollapsibleSection text={t('page.projects.editProject.parentProject')}>
        <Autocomplete
          sx={{ pl: leftPadding }}
          multiple
          id="parent-autocomplete"
          size="small"
          disableCloseOnSelect
          loading={isParentsProjectLoading}
          options={displayedProjects}
          value={parentName}
          onChange={(_, value, __) => {
            setSelectedFilters(prev => ({
              ...prev,
              parents: value.map(project => project.id),
            }))
            setParentName(value)
          }}
          ListboxProps={{
            style: { maxHeight: 200 },
            onScroll: onScroll,
          }}
          getOptionLabel={option => option.acronym}
          renderOption={(props, option, { selected }) => {
            const { key, ...restProps } = props
            return (
              <StyledListItem key={key} {...restProps}>
                <Checkbox icon={icon} checkedIcon={checkedIcon} style={{ marginRight: 8 }} checked={selected} />
                {option.acronym}
              </StyledListItem>
            )
          }}
          renderInput={params => (
            <TextField
              size="small"
              fullWidth
              inputProps={{ ...params.inputProps, 'aria-label': t('page.projects.editProject.parentProject') }}
              InputProps={params.InputProps}
              onChange={event => {
                handleParentProjectSearch(event.target.value)
              }}
              label={t('page.projects.editProject.parentProject')}
              placeholder={t('page.projects.editProject.parentProject')}
            />
          )}
        />
      </CollapsibleSection>
      <CollapsibleSection text={t('page.projects.filterPopup.favorite')}>
        <FormControlLabel
          sx={{ pl: leftPadding }}
          control={<Checkbox checked={selectedFilters.favorite} onChange={handleFavoriteChange} />}
          label={t('page.projects.filterPopup.favorite')}
        />
      </CollapsibleSection>
      <CollapsibleSection text={t('page.projects.filterPopup.projectType')}>
        <FormControlLabel
          sx={{ pl: leftPadding }}
          control={
            <Checkbox
              checked={selectedFilters.operational === true}
              onChange={event => handleOperationalChange(event, 'operational')}
            />
          }
          label={t('page.projects.filterPopup.operational')}
        />
        <FormControlLabel
          sx={{ pl: leftPadding }}
          control={
            <Checkbox
              checked={selectedFilters.operational === false}
              onChange={event => handleOperationalChange(event, 'nonOperational')}
            />
          }
          label={t('page.projects.filterPopup.notOperational')}
        />
      </CollapsibleSection>
    </FilterMenu>
  )
}

export default FilterProjectsMenu
